/**
 * @author 冷 (https://github.com/LengYXin)
 * @email lengyingxin8966@gmail.com
 * @create date 2021-09-02 16:49:07
 * @modify date 2021-09-02 16:49:07
 * @desc 产品控制器
 */
import lodash from 'lodash';
import { BindAll } from 'lodash-decorators';

import { EnumAPI } from '../../apis';
import { AjaxBasics } from '../../helpers';
import { BasesController } from '../bases';
import { onCreateFactoryAuth } from '../system/auth';
import { onCreateFactoryUser } from '../system/userModel';
import { AdvisoryProductController } from './advisory';
import { ChannelDetailsController } from './channelDetails';
import { ChannelsController } from './channels';
import { ChannelSearchController } from './channelSearch';
import { PointsProductController } from './pointsProduct';
import { PromotionalController } from './promotional';
import { SearchParams } from './searchParams';
import { SeriesProductController } from './series';

@BindAll()
export class ProductController<T = any> extends BasesController<T> {
  constructor() {
    super();
    this.changeToOriginalController();
  }
  mode: 'normal' | 'points';
  // 普通商品配置
  changeToOriginalController() {
    if (this.mode === 'normal') {
      return;
    }
    this.mode = 'normal';
    this.reset({
      dataKey: 'productId',
      infinite: true,
      pagination:
        this.User.BU == 'BU006'
          ? EnumAPI.product_search_ISG
          : EnumAPI.product_search,
      details:
        this.User.BU == 'BU006'
          ? EnumAPI.product_detail_ISG
          : EnumAPI.product_detail,
      detailsParams: {
        valueGetter: this.detailsValueGetter,
      },
      paginationParams: {
        currentKey: 'pageNo',
        pageSizeKey: 'pageSize',
        response: {
          // dataSource: 'list.productList',
          // total: 'count',
          valueGetter: (res) => {
            const dataSource = lodash.get(res, 'list.productList', []);
            if (
              !lodash.isEmpty(dataSource) &&
              dataSource[0].tradeType != 2 &&
              this.User.BU != 'BU006'
            ) {
              this.onGetActivePrice(dataSource);
            }
            return {
              dataSource: dataSource,
              total: lodash.get(res, 'count', 0),
            };
          },
        },
      },
    });
  }
  // 积分商品配置
  changeToPointsController() {
    if (this.mode === 'points') {
      return;
    }
    this.mode = 'points';
    this.reset(this.PointsProductController.resetConfig);
  }

  /**
   * 用户信息
   * @memberof SystemController
   */
  readonly User = onCreateFactoryUser();

  /**
   * 鉴权信息
   * @memberof SystemController
   */
  readonly Auth = onCreateFactoryAuth();

  /**
   * @desc 获取同系列产品
   * @param entity 详情数据
   */
  async onGetSameSeries(entity: any) {
    const body = {
      'channelProductId': entity.agreementproductId,
      'channelProductType': entity.channelProductType,
    };
    // L2走游客逻辑
    if (!(this.User.isTourists || this.User.isL2)) {
      const response = await AjaxBasics.request({
        ...EnumAPI.product_sameSeries,
        body,
      });
      this.Details.Model.merge({
        // 同系列产品
        _Series: parsesSeries(response),
        // 当前同系列产品
        _SerieFind: parsesSerieFind(entity, response),
      });
    } else {
      const response = entity.recommentSpecProductList;
      lodash.assign(entity, {
        // 同系列产品
        _Series: parsesSeries(response),
        // 当前同系列产品
        _SerieFind: parsesSerieFind(entity, response),
      });
    }
  }

  /**
   * @desc 获取详情页推荐产品
   * @param entity 详情数据
   */
  async onGetDetailRecommend(entity: any) {
    const body = {
      'productId': entity.agreementproductId,
    };
    const response = await AjaxBasics.request({
      ...EnumAPI.product_recommend,
      body,
    });
    let recommendList = lodash.chunk(parsesSeries(response), 4);
    this.Details.Model.merge({
      // 同系列产品
      _Recommend: recommendList,
      _IsShowRecommend: !lodash.isEmpty(parsesSeries(response)),
    });
  }

  /**
   * @desc 详情页点击量统计
   * @param entity 详情数据
   */
  async statisticsCount(agreementproductId: any, viewType) {
    const body = {
      'productId': agreementproductId,
      viewType,
    };
    const response = await AjaxBasics.request({
      ...EnumAPI.product_count,
      body,
    });
  }

  /**
   * @desc 详情页统计曝光量
   * @param entity 详情数据
   */
  async statisticsShowCount(data: any, displayType, currentProductId) {
    const body = {
      data,
      displayType,
      currentProductId,
    };
    const response = await AjaxBasics.request({
      ...EnumAPI.product_show_count,
      body,
    });
  }

  /**
   * @desc 获取产品列表活动价格
   * @param entity 产品列表
   */
  async onGetActivePrice(dataSource) {
    const response = await AjaxBasics.request<Array<any>>({
      ...EnumAPI.product_search_price,
      body: lodash.map(dataSource, (item) => ({
        price: item.price,
        campaignProductId: item.campaignProductId,
        channelProductType: item.channelProductType,
        channelProductId: item.productId,
        dealerId: item.dealerId,
      })),
    });
    lodash.map(response, (item) => {
      // item.price=1
      // 修改数组中的对象 根据 entity.dataKey 匹配
      this.Pagination.onUpdate({
        ...item,
        //    dataKey: 'productId', 用于查找 对应数据
        productId: item.channelProductId,
      });
    });
  }
  /**
   * @dec 产品推广
   * @memberof ProductController
   */
  PromotionalController = new PromotionalController();
  /**
   * 合并2个接口数据
   * @param entity
   * @param response
   * @returns
   */
  async onDetailSpecSelect(body: {
    channelProductId: any;
    levelCode: any;
    partsList: Array<{ channelBindId: any; isSelected: boolean }>;
  }) {
    const { productPartsVoList } = await AjaxBasics.request({
      ...EnumAPI.product_detail_select,
      body: body,
    });
    this.Details.Model.merge({
      productPartsVoList,
      // 绑定配件
      _FittingsBind: parsesFittings({ productPartsVoList }, 1),
      // 可选配件
      _FittingsOptional: parsesFittings({ productPartsVoList }, 0),
    });
  }
  /**
   * 详情数据 自定义获取 数据重组
   * @param response
   * @returns
   */
  detailsValueGetter(response) {
    lodash.assign(response, {
      // 轮播图
      _SwiperImgs: parsesSwiperImgs(response),
      // 渲染内容 图片
      _ContentImgs: parsesContentImgs(response),
      // 渲染内容
      _Content: parsesContent(response),
      // 参数配置
      _Configuration: parsesConfiguration(response),
      //组合产品参数配置
      _CombinedProductParameterList:
        parsesCombinedProductParameterList(response),
      // 同系列产品
      _Series: [],
      // 当前同系列产品
      _SerieFind: {},
      //推荐产品列表
      _Recommend: [],
      //推荐产品列表
      _IsShowRecommend: false,
      // 绑定配件
      _FittingsBind: parsesFittings(response, 1),
      // 可选配件
      _FittingsOptional: parsesFittings(response, 0),
      // 弹窗图片
      _skuProductImg: parsesProductImg(response),
      // 计算价格
      _price: parsesPrices(response),
      _promotionInfo: parsePromotionInfo(response),
      //预约按钮
      _isReserve: getIsReserve(response),
      _isScore: getIsScore(response),
      productPrice: parsesPrices(response),
      productImg: getProductImg(response),
      id: getId(response),
    });
    // 获取同系列产品
    if (this.User.BU != 'BU006') {
      this.onGetSameSeries(response);
    }
    //获取推荐产品列表
    if (
      // L2走游客逻辑
      !(this.User.isTourists || this.User.isL2) &&
      this.User.isSMB &&
      this.User.isCooperativePartner
    ) {
      this.onGetDetailRecommend(response);
    }
    return response;
  }
  /**
   * 搜索参数
   * @memberof ProductController
   */
  readonly SearchParams = new SearchParams({
    storageKey: 'product_search_params',
  });

  public BU = this.User.BU;
  /**
   * 初始化搜索参数
   * @memberof ProductController
   */
  async initSearchParams() {
    if (
      lodash.size(lodash.get(this.SearchParams.value, 'Label')) &&
      lodash.size(lodash.get(this.SearchParams.value, 'Category')) &&
      this.BU === this.User.BU
    ) {
      return;
    }

    this.BU = this.User.BU;
    const getCategory = AjaxBasics.request({
      ...EnumAPI.product_category,
      body: {
        buType: this.User.buRole,
        platform: 'BPPORTAL',
      },
    });
    const Category = await getCategory;
    // const getLabel = AjaxBasics.request({...EnumAPI.product_label,
    //   body:{id:lodash.get(Category,['children','0','id'],'')}});
    // const Label = await getLabel;
    // const Label = this.getLabelById(lodash.get(Category,['children','0','id'],''))
    this.SearchParams.setSearchParams(
      lodash.get(Category, 'children'),
      // lodash.get(Label, 'goodsParameterDTOS'),
    );
  }

  /**
   * @dec 根据目录id获取label
   */
  async getLabelById(id) {
    const getLabel = AjaxBasics.request({
      ...EnumAPI.product_label,
      body: { id },
    });
    const Label = await getLabel;
    return lodash.get(Label, 'goodsParameterDTOS');
  }
  /**
   *
   * @dec 目录页面
   *
   */
  ChannelsController = new ChannelsController();
  //目录搜索页面
  ChannelSearchController = new ChannelSearchController();
  // 渠道系列产品
  SeriesProductController = new SeriesProductController();
  //渠道产品详情
  ChannelDetailsController = new ChannelDetailsController();
  //渠道产品留资
  AdvisoryProductController = new AdvisoryProductController();
  // 积分产品
  PointsProductController = new PointsProductController(this);
}

/**
 * @dec 产品默认图片
 */
export const defaultProductImg: string =
  'https://ossvip.lenovo.com.cn/bpp-poa-prod/bpp-poa-bucket/ce1aa380882f4a08a6c9e8b2ccba3d97.png';
/**
 * 解析 轮播图
 * @param {*} entity 详情数据
 * @return {*}
 * @memberof ProductController
 */
function parsesSwiperImgs(entity) {
  const SwiperImgs = lodash.filter(
    lodash.get(
      entity,
      'productLabelsByCodeResponseVOList[0].productLabelsFiles',
      [],
    ),
    ['fileType', 13],
  );
  return lodash.isEmpty(SwiperImgs)
    ? [{ fileUrl: defaultProductImg }]
    : SwiperImgs;
}
/**
 * 解析 渲染内容 图片
 * @param {*} entity 详情数据
 * @return {*}
 * @memberof ProductController
 */
function parsesContentImgs(entity) {
  const ContentImgs = lodash.filter(
    lodash.get(
      entity,
      'productLabelsByCodeResponseVOList[0].productLabelsFiles',
    ),
    ['fileType', 32],
  );
  return lodash.isEmpty(ContentImgs)
    ? [{ fileUrl: defaultProductImg }]
    : ContentImgs;
}
/**
 * 解析 渲染内容
 * @param {*} entity 详情数据
 * @return {*}
 * @memberof ProductController
 */
function parsesContent(entity) {
  return lodash.get(
    entity,
    'productLabelsByCodeResponseVOList[0].productDetailHtmlMobile',
  );
}
/**
 * 解析 参数配置
 * @param {*} entity 详情数据
 * @return {*}
 * @memberof ProductController
 */
function parsesConfiguration(entity) {
  return lodash.get(entity, 'configuration[0].configurationParameterList');
}
/**
 * 解析 参数配置
 * @param {*} entity 详情数据
 * @return {*}
 * @memberof ProductController
 */
function parsesCombinedProductParameterList(entity) {
  return lodash.get(entity, 'combinedProductParameterList');
}
/**
 * 解析 同系列产品
 * @param {*} entity 详情数据
 * @return {*}
 * @memberof ProductController
 */
function parsesSeries(entity) {
  return Array.isArray(entity) ? entity : [];
}
/**
 * 解析 当前系列 同系列产品
 * @param {*} entity 详情数据
 * @param {*} list 同系列产品数据
 * @return {*}
 * @memberof ProductController
 */
function parsesSerieFind(entity, list) {
  // return lodash.find(lodash.get(entity, 'recommentSpecProductList'), ['protocolProductId', entity.agreementproductId]) || lodash.head(lodash.get(entity, 'recommentSpecProductList'))
  return (
    lodash.find(list, ['channelProductId', entity?.agreementproductId]) || {}
  );
}
/**
 * 解析 配件
 * @param {*} entity 详情数据
 * @return {*}
 * @memberof ProductController
 */
function parsesFittings(entity, optional: 0 | 1) {
  return lodash.filter(lodash.get(entity, 'productPartsVoList'), [
    'optional',
    optional,
  ]);
}
/**
 * 解析 弹窗图片
 * @param {*} entity 详情数据
 * @return {*}
 * @memberof ProductController
 */
function parsesProductImg(entity) {
  return lodash.get(
    lodash.find(
      lodash.get(
        entity,
        'productLabelsByCodeResponseVOList[0].productLabelsFiles',
        [],
      ),
      ['fileType', 11],
    ),
    'fileUrl',
    defaultProductImg,
  );
}
/**
 * 解析 重新计算价格
 * @param {*} entity 详情数据
 * @return {*}
 * @memberof ProductController
 */
function parsesPrices(entity) {
  if (entity.tradeType == 2) {
    return parseFloat(entity.price);
  }
  if (entity.channelProductType == 2) {
    return parseFloat(entity.price).toFixed(2);
  } else {
    let normalPrice = getNormalPrice(entity);
    return (parseFloat(entity.price) + normalPrice).toFixed(2);
  }
}
function getNormalPrice(entity) {
  let normal = lodash.filter(lodash.get(entity, 'productPartsVoList'), {
    'isSelected': true,
    'optional': 1,
  });
  let normalPrice = lodash.reduce(
    lodash.map(
      normal,
      (item) => parseFloat(item.price) * parseFloat(item.odBindAmount),
    ),
    (total, num) => total + num,
    0,
  );
  return normalPrice;
}

/**
 * 解析 活动信息
 * @param {*} entity 详情数据
 * @return {*}
 * @memberof ProductController
 */
function parsePromotionInfo(entity) {
  const { promotionInfo, isScore } = entity;
  let originalPrice = entity.price;
  let isHaveActivity = !!promotionInfo;
  let nowTime = Date.now();
  if (isHaveActivity) {
    const { startTime, canOrderTime, endTime, price, reserveBeginTime } =
      promotionInfo;
    let isInActivity = lodash.inRange(
      nowTime,
      dateToTime(startTime),
      dateToTime(endTime),
    );
    let isPriceGt = lodash.gt(Number(originalPrice), Number(price));
    promotionInfo.isPriceGt = isPriceGt;
    promotionInfo.isInActivity = isInActivity; //是否在活动期
    promotionInfo.isPreheat = false; //是否为活动预热期;
    promotionInfo.isReserveBefor = false; //是否为活动预约期前;
    if (isInActivity) {
      promotionInfo.isPreheat = lodash.inRange(
        nowTime,
        dateToTime(startTime),
        dateToTime(canOrderTime),
      );
      promotionInfo.isReserveBefor = lodash.inRange(
        nowTime,
        dateToTime(startTime),
        dateToTime(reserveBeginTime),
      );
    }
    let normalPrice = getNormalPrice(entity);
    promotionInfo.showPrice = (
      parseFloat(promotionInfo.price) + normalPrice
    ).toFixed(2);
    promotionInfo.price = isScore
      ? parseFloat(promotionInfo.price)
      : parseFloat(promotionInfo.price).toFixed(2);
  }
  return promotionInfo;
}

function dateToTime(datestr) {
  return new Date(datestr).getTime();
}

function getIsReserve(entity) {
  const { buttonList } = entity;
  return lodash.includes(buttonList, 6);
}

function getIsScore(entity) {
  let tradeType = lodash.get(entity, 'tradeType');
  return tradeType == 2 ? true : false;
}

function getId(entity) {
  return lodash.get(entity, 'agreementproductId');
}

function getProductImg(entity) {
  let imgList = parsesSwiperImgs(entity);
  return imgList && imgList.length
    ? imgList[0].fileUrl
    : 'https://ossvip.lenovo.com.cn/bpp-poa-prod/bpp-poa-bucket/ce1aa380882f4a08a6c9e8b2ccba3d97.png';
}
